import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody, 
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Alert,
  Container,
  Pagination,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  Inventory,
  LocationOn,
  CalendarToday,
} from "@mui/icons-material";
import { racksAPI } from "../../services/API";
import { useSnackbar } from "notistack";
import { useAuth } from "../../contexts/AuthContext";

export const RackDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasAnyRole } = useAuth();

  const [loading, setLoading] = useState(true);
  const [rack, setRack] = useState(null);
  const [stockPage, setStockPage] = useState(1);
  const [stockItemsPerPage] = useState(10);

  useEffect(() => {
    fetchRack();
  }, [id]);

  const fetchRack = async () => {
    try {
      setLoading(true);
      const response = await racksAPI.getById(id);
      setRack(response.data.data.rack);
    } catch (error) {
      console.error("Error fetching rack:", error);
      enqueueSnackbar("Error fetching rack details", { variant: "error" });
      navigate("/hospital/pharmacy/racks");
    } finally {
      setLoading(false);
    }
  };

  const getZoneColor = (zone) => {
    const colors = {
      general: "primary",
      cold_storage: "info",
      controlled: "warning",
      hazardous: "error",
      quarantine: "secondary",
    };
    return colors[zone] || "default";
  };

  const formatZoneName = (zone) => {
    return zone
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getUtilizationColor = (utilization) => {
    if (utilization >= 90) return "error";
    if (utilization >= 75) return "warning";
    return "success";
  };

  // Paginate stock items
  const paginatedStockItems = rack?.stockItems?.slice(
    (stockPage - 1) * stockItemsPerPage,
    stockPage * stockItemsPerPage
  ) || [];

  const totalStockPages = Math.ceil((rack?.stockItems?.length || 0) / stockItemsPerPage);

  const handleStockPageChange = (event, value) => {
    setStockPage(value);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!rack) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="h4" color="error" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
          Rack not found
        </Typography>
        <Button 
          onClick={() => navigate("/hospital/pharmacy/racks")} 
          sx={{ mt: 2, fontSize: '1.1rem' }}
          variant="contained"
        >
          Back to Racks
        </Button>
      </Box>
    );
  }

  const utilization =
    rack.capacity > 0 ? (rack.currentOccupancy / rack.capacity) * 100 : 0;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
        fontSize: '16px',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button 
              startIcon={<ArrowBack />} 
              onClick={() => navigate("/hospital/pharmacy/racks")}
              sx={{ fontSize: '1.1rem' }}
            >
              Back
            </Button>
            <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
              {rack.name}
            </Typography>
            <Chip
              label={rack.isActive ? "Active" : "Inactive"}
              color={rack.isActive ? "success" : "default"}
              size="large"
              sx={{ 
                fontSize: '1rem',
                fontWeight: 600,
                height: 40
              }}
            />
          </Box>
          {hasAnyRole(["admin", "inventory_manager"]) && (
            <Button
              startIcon={<Edit />}
              onClick={() => navigate(`/hospital/pharmacy/racks/${id}/edit`)}
              variant="contained"
              sx={{ fontSize: '1.1rem', px: 3, py: 1 }}
            >
              Edit Rack
            </Button>
          )}
        </Box>

        <Grid container spacing={3}>
          {/* Main Details */}
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
                  Rack Information
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                      Rack Name
                    </Typography>
                    <Typography variant="h5" fontWeight="medium" sx={{ fontSize: { xs: '1.2rem', md: '1.4rem' } }}>
                      {rack.name}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                      Zone
                    </Typography>
                    <Chip
                      label={formatZoneName(rack.location.zone)}
                      color={getZoneColor(rack.location.zone)}
                      size="large"
                      sx={{ 
                        fontSize: '1rem',
                        fontWeight: 600,
                        height: 40
                      }}
                    />
                  </Grid>

                  {rack.description && (
                    <Grid item xs={12}>
                      <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                        Description
                      </Typography>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>{rack.description}</Typography>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                      Location Details
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      <LocationOn fontSize="large" color="action" />
                      <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                        {[
                          rack.location.aisle,
                          rack.location.row,
                          rack.location.section,
                        ]
                          .filter(Boolean)
                          .join(" - ") || "Not specified"}
                      </Typography>
                    </Box>
                  </Grid>

                  {rack.temperatureRange &&
                    (rack.temperatureRange.min || rack.temperatureRange.max) && (
                      <Grid item xs={12}>
                        <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                          Temperature Range
                        </Typography>
                        <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                          {rack.temperatureRange.min && rack.temperatureRange.max
                            ? `${rack.temperatureRange.min}°C - ${rack.temperatureRange.max}°C`
                            : rack.temperatureRange.min
                            ? `Min: ${rack.temperatureRange.min}°C`
                            : `Max: ${rack.temperatureRange.max}°C`}
                        </Typography>
                      </Grid>
                    )}
                </Grid>
              </CardContent>
            </Card>

            {/* Capacity & Utilization */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
                  Capacity & Utilization
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                      Current Occupancy: {rack.currentOccupancy} / {rack.capacity}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.3rem' }}>
                      {utilization.toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(utilization, 100)}
                    color={getUtilizationColor(utilization)}
                    sx={{ height: 12, borderRadius: 5 }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 1,
                    }}
                  >
                    <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1rem' }}>
                      Available: {rack.capacity - rack.currentOccupancy}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1rem' }}>
                      Capacity: {rack.capacity}
                    </Typography>
                  </Box>
                </Box>

                {utilization >= 90 && (
                  <Alert severity="warning" sx={{ fontSize: '1.1rem' }}>
                    This rack is nearly full. Consider transferring some stock to
                    other racks.
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Stock Items with Pagination */}
            {rack.stockItems && rack.stockItems.length > 0 && (
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
                      Current Stock ({rack.stockItems.length})
                    </Typography>
                    {totalStockPages > 1 && (
                      <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                        Page {stockPage} of {totalStockPages}
                      </Typography>
                    )}
                  </Box>

                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: '1.1rem', fontWeight: 600, py: 2 }}>Medicine</TableCell>
                          <TableCell sx={{ fontSize: '1.1rem', fontWeight: 600, py: 2 }}>Batch Number</TableCell>
                          <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 600, py: 2 }}>Quantity</TableCell>
                          <TableCell sx={{ fontSize: '1.1rem', fontWeight: 600, py: 2 }}>Expiry Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedStockItems.map((item, index) => (
                          <TableRow key={index} hover>
                            <TableCell sx={{ py: 2.5 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                {item.medicine.name}
                              </Typography>
                              <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ fontSize: '1rem' }}
                              >
                                {item.medicine.genericName}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2.5 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                {item.batchNumber}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2.5 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                {item.availableQuantity} / {item.quantity}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2.5 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <CalendarToday fontSize="medium" color="action" />
                                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                  {new Date(item.expiryDate).toLocaleDateString()}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Stock Items Pagination */}
                  {totalStockPages > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                      <Pagination
                        count={totalStockPages}
                        page={stockPage}
                        onChange={handleStockPageChange}
                        color="primary"
                        size="large"
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {(!rack.stockItems || rack.stockItems.length === 0) && (
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Inventory
                      sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                    />
                    <Typography variant="h4" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
                      No Stock Items
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                      This rack is currently empty. Stock will appear here when
                      items are assigned to this rack.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 3, position: 'sticky', top: 100 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
                  Quick Actions
                </Typography>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Inventory />}
                  sx={{ mb: 2, fontSize: '1.1rem', py: 1.5 }}
                  onClick={() => navigate("/hospital/pharmacy/inventory")}
                >
                  View Inventory
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<LocationOn />}
                  sx={{ fontSize: '1.1rem', py: 1.5 }}
                  onClick={() => navigate("/hospital/pharmacy/racks")}
                >
                  View All Racks
                </Button>
              </CardContent>
            </Card>

            <Card sx={{ position: 'sticky', top: 300 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
                  Rack Statistics
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                    Utilization Rate
                  </Typography>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color={getUtilizationColor(utilization)}
                    sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}
                  >
                    {utilization.toFixed(1)}%
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                    Available Space
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
                    {rack.capacity - rack.currentOccupancy}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                    Stock Items
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
                    {rack.stockItems?.length || 0}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                    Status
                  </Typography>
                  <Typography variant="h5" fontWeight="medium" sx={{ fontSize: '1.2rem' }}>
                    {rack.isActive ? "Active" : "Inactive"}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};